// Turning a stored canonical value (kilograms, grams, centimetres, kilocalories)
// plus a display unit into the text a person reads. This is the ONE place that
// does it, so that everywhere the same value is shown — the app's own screens and
// the emails the server composes — shows the same number.
//
// It lives in shared/ rather than in the client for that reason: the coach
// notification emails in server/functions render logged set weights, and a second
// copy of this arithmetic on the server would drift away from what the client
// draws (issue #4903, where the email printed a kilogram number under a "lb"
// label). Nothing here touches Angular, translations or the DOM.
//
// The unit tables it works from are in ./measure-configs. The client-only parts —
// translating a unit's description into the user's language — stay in
// client/src/util/formatting.ts, which re-exports everything below so its existing
// call sites are unaffected.

import { formatLocaleFloat, LocaleSettings, roundToPrecision, toFraction } from './shared-util';
import { GlobalMeasurementTypes, MeasurementType, MeasureUnitConfig } from './measure-configs';


export function findBestUnitConfig(configs: MeasureUnitConfig[], locale: LocaleSettings, unit: string): MeasureUnitConfig {
  // try finding the config directly by specified id (coming from specific value, or from general user settings)
  let config = configs.find(c => c.id == unit);
  // if that fails
  if (!config) {
    // ...determine which one best fits current locale
    let bestConfig = null;
    let bestConfigLen = 1000; // config with shortest list of countries wins (since it means it is more specific)
    // for each config possible
    for (let i = 0; i < configs.length; i++) {
      let c = configs[i];
      let localesLen = 100000;
      let foundLocale = false;
      // find if this locale is mentioned in this config
      if (c.locales) {
        localesLen = c.locales.length;
        foundLocale = c.locales.some(l => l === locale.localeName);
      }

      // if yes, and this is better
      if (foundLocale && localesLen < bestConfigLen) {
        bestConfig = c;
        bestConfigLen = localesLen;
        // if no config found yet, at least remember this one
      } else if (!foundLocale && bestConfig == null) {
        bestConfig = c;
      }
    }

    config = bestConfig;
  }
  // safety, if none else found - use first
  if (!config) {
    config = configs[0];
  }
  return config;
}

/**
 * Resolve a measurement type — given either as one of the ids in
 * `GlobalMeasurementTypes` or as the type object itself — together with the unit
 * configuration that best fits the caller's requested unit and locale.
 *
 * An id that is not in the table THROWS, and that is deliberate. It used to fall
 * back to the first entry of the table, which is `bodyweight_kg`, so a typo'd or
 * renamed id produced a number computed with body weight's conversion factor and
 * body weight's rounding, labelled with one of body weight's own units. The number
 * looked entirely plausible, nothing was logged and no telemetry was sent, so the
 * only way anybody could find out was a person noticing a wrong figure on screen.
 *
 * Nothing a USER does can reach this branch: every caller passes a literal id or a
 * type object, so the only way to get here is a developer's mistake. That is
 * exactly the condition worth failing loudly on, and CLAUDE.md's "unexpected
 * conditions must surface" rule leaves no other option here — `shared/` has no
 * Sentry runtime, so propagating to the caller is the one channel available.
 */
export function findBestUnitConfigForType(type: string | MeasurementType, locale: LocaleSettings, unit: string) {
  let measurementType: MeasurementType;
  if (typeof type === 'object') {
    measurementType = type;
  } else {
    measurementType = GlobalMeasurementTypes.find(m => m.id == type);
    if (!measurementType) {
      throw new Error(
        `no measurement type '${type}' — the valid ids are the GlobalMeasurementTypes entries in shared/src/measure-configs.ts`,
      );
    }
  }
  let measurementUnitConfig = findBestUnitConfig(measurementType.configs, locale, unit);
  return { measurementType, measurementUnitConfig };
}

// How many of a dual-unit config's second unit make one of its first unit -- 14 pounds to a
// stone, 12 inches to a foot. Only meaningful for a config that actually has a second unit;
// returns undefined for a single-unit config, whose value is not split over two fields.
export function dualUnitRatio(config: MeasureUnitConfig): number | undefined {
  if (config.value1 == undefined || config.value2 == undefined) {
    return undefined;
  }
  return Math.round(config.value1 / config.value2);
}

// convert from actual number into the text fields
export function canonValueToTexts_global(
  canonValue: number,
  type: MeasurementType,
  config: MeasureUnitConfig,
  locale: LocaleSettings,
  opts?: { makeBallpark?: boolean; group?: boolean },
): string[] {
  let strings: string[] = ['', ''];
  let makeBallpark = opts && opts.makeBallpark;
  // group thousands by default (display); editable-input callers pass group=false
  // so a value they type is never shown grouped. #4204
  let group = opts?.group ?? true;
  let forceDecimals = type?.forceDecimals;

  if (canonValue == undefined) {
    return strings;
  }

  if (config.value1 == undefined) {
    return strings;
  } else if (config.value2 == undefined) {
    let val1 = (canonValue * type.nominalUnit) / config.value1;
    if (config.fractionDenominatorMax) {
      strings[0] = toFraction(val1, { maxDenominator: config.fractionDenominatorMax, precision: 0.001, makeBallpark });
    }
    if (!strings[0]) {
      val1 = roundToPrecision(val1, type.precision);
      strings[0] = formatLocaleFloat(val1, locale, forceDecimals, group);
    }
    strings[1] = '';
  } else {
    let unitRatio = dualUnitRatio(config);

    let val2 = (canonValue * type.nominalUnit) / config.value2;
    val2 = roundToPrecision(val2, type.precision);
    let val1 = Math.floor(val2 / unitRatio);
    val2 -= val1 * unitRatio;
    val2 = Math.max(val2, 0); // clamp against still eventual small negative values
    // Round val2 again after subtraction to ensure proper decimal places
    val2 = roundToPrecision(val2, type.precision);
    strings[0] = val1.toFixed(0);
    strings[1] = formatLocaleFloat(val2, locale, forceDecimals, group);
  }

  return strings;
}

export function formatMeasure(
  cannonValue: number,
  type: string | MeasurementType,
  locale: LocaleSettings,
  unit: string,
): string {
  const thinSpace = '\u202F'; // thin non-breaking space
  let { measurementType, measurementUnitConfig } = findBestUnitConfigForType(type, locale, unit);
  let [text1, text2] = canonValueToTexts_global(cannonValue, measurementType, measurementUnitConfig, locale);
  let result = `${text1}${thinSpace}${measurementUnitConfig.unit1}`;
  if (measurementUnitConfig.unit2) {
    result += `${thinSpace}${text2}${thinSpace}${measurementUnitConfig.unit2}`;
  }
  return result;
}
